import type {ReactNode} from 'react'
import {CommentDiscussionIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button} from '@primer/react'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {Link} from '@github-ui/react-router'

import {ForYouItem} from '../shared/ForYouItem'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {pullRequestsLayoutRoute} from '../../../../routes/layout-route'
import {pullRequestsChangesRoute} from '../../../../routes/files-changed-routes'
import {DelegatedFromTeams} from './DelegatedFromTeams'
import {useReviewRequestedData, type ReviewRequestedData} from './use-review-requested-data'

// Surfaces the viewer's requested-review state in the "Needs attention" section, with a
// shortcut to start reviewing on the Files changed page.
export function ReviewRequested() {
  const data = useReviewRequestedData()
  const {owner, repo, pr_number} = useRouteParams(pullRequestsLayoutRoute)
  if (!data) {
    return null
  }

  const changesPath = pullRequestsChangesRoute.generatePath({owner, repo, pr_number})
  const {title, description} = messageFor(data)

  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={CommentDiscussionIcon} variant="warning" />}
      title={title}
      itemName="review-requested"
      description={description}
      actionSlot={<ReviewRequestedActions data={data} changesPath={changesPath} />}
    />
  )
}

function messageFor(data: ReviewRequestedData): {title: string; description?: ReactNode} {
  switch (data.kind) {
    case 'delegated': {
      return {
        title: 'You have been assigned to review',
        description:
          data.delegatedFromTeams.length > 0 ? <DelegatedFromTeams teams={data.delegatedFromTeams} /> : undefined,
      }
    }
    case 'codeowner':
      return {title: 'Your review has been requested as a code owner'}
    case 'direct':
      return {title: 'Your review has been requested'}
  }
}

function ReviewRequestedActions({data, changesPath}: {data: ReviewRequestedData; changesPath: string}) {
  // Code owner: offer the full review plus a shortcut filtered to the viewer's own files. Use a
  // single-width menu button rather than two side-by-side buttons so the action always fits inside
  // the fixed-width trailing column and never clips at 320px / 400% zoom (WCAG 1.4.10 Reflow).
  if (data.kind === 'codeowner') {
    const viewerLogin = getCurrentUserLogin()
    const ownedFilesPath = viewerLogin ? `${changesPath}?owned-by[]=${encodeURIComponent(viewerLogin)}` : changesPath
    return (
      <ActionMenu>
        <ActionMenu.Button size="small" trailingAction={TriangleDownIcon}>
          Review
        </ActionMenu.Button>
        <ActionMenu.Overlay align="end">
          <ActionList>
            <ActionList.LinkItem as={Link} to={changesPath}>
              Review all files
            </ActionList.LinkItem>
            <ActionList.LinkItem as={Link} to={ownedFilesPath}>
              Review just your files
            </ActionList.LinkItem>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    )
  }

  return (
    <Button as={Link} to={changesPath} size="small">
      Review
    </Button>
  )
}
